import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SkeletonPlaceholder, Tag } from '@carbon/react';
import IssueCard from 'src/components/Issue/IssueCard';
import { IssueEntity } from 'src/services/apis/types';
import { getIssueStatusColor, getIssueStatusText, IssueStatusEnum } from 'src/utils/issue';

interface IssueLaneProps {
  status: IssueStatusEnum;
  rows: IssueEntity[];
  total: number;
  isLoading: boolean;
  canWrite: boolean;
  onView: (issue: IssueEntity) => void;
  onEdit: (issue: IssueEntity) => void;
  onDelete: (issue: IssueEntity) => void;
}

const IssueLane = React.memo(
  ({ status, rows, total, isLoading, canWrite, onView, onEdit, onDelete }: IssueLaneProps) => {
    const { t } = useTranslation();
    // The lane itself is a drop target, so an empty lane can still receive a card — a
    // SortableContext with no items has nothing to drop onto.
    const { setNodeRef, isOver } = useDroppable({ id: `lane-${status}` });

    return (
      <section className={`issue-lane${isOver ? ' issue-lane--over' : ''}`}>
        <header className={'issue-lane__header'}>
          <Tag type={getIssueStatusColor(status)}>{t(getIssueStatusText(status))}</Tag>
          {/* The count is the lane TOTAL, not the rows rendered: a lane can read 47 while
            showing the first 50-card page. */}
          <span className={'issue-lane__count'}>{total}</span>
        </header>
        <ul ref={setNodeRef} className={'issue-lane__cards'}>
          {isLoading ? (
            <SkeletonPlaceholder className={'issue-lane__skeleton'} />
          ) : (
            <SortableContext items={rows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
              {rows.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  canWrite={canWrite}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </SortableContext>
          )}
        </ul>
      </section>
    );
  }
);

IssueLane.displayName = 'IssueLane';

export default IssueLane;
